import type { ApiClient } from '@up2d8/api-client';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, Importance } from '@capacitor-firebase/messaging';
import { appPathFor } from './notification-target';

/**
 * Native pushmeldingen in de app (MOB-1).
 *
 * Het tegenstuk van web push in `use-push.ts`, met dezelfde drie handelingen: toestemming vragen,
 * het toestel aanmelden bij de server, en weer afmelden. De server stuurt via Firebase Cloud
 * Messaging (`notifications`, kanaal `fcm`), en heeft daarvoor een **Firebase**-token nodig, ook op
 * een iPhone. Daarom `@capacitor-firebase/messaging` en niet `@capacitor/push-notifications`: dat
 * laatste geeft op iOS het token van Apple, waar Firebase niets mee kan.
 *
 * Alleen aanroepen als `isNativeApp()`.
 */

/** Het token van dit toestel, om het bij het afmelden weer weg te kunnen halen. */
const TOKEN_KEY = 'up2d8.native-push.token';

export type NativePermission = 'granted' | 'denied' | 'prompt';

export function storedNativeToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Zonder opslag melden we het toestel bij elke start opnieuw aan; dat is idempotent.
  }
}

export async function nativePermission(): Promise<NativePermission> {
  const { receive } = await FirebaseMessaging.checkPermissions();
  if (receive === 'granted') return 'granted';
  if (receive === 'denied') return 'denied';
  return 'prompt';
}

/**
 * De meldingskanalen van Android, met dezelfde sleutels als de server
 * (`notifications/internal/domain/fcm-message.ts`). Zo kan iemand gesprekken stil zetten zonder
 * dat het noodkanaal mee stil gaat. Op iOS bestaan kanalen niet.
 */
async function ensureAndroidChannels(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  await FirebaseMessaging.createChannel({
    id: 'berichten',
    name: 'Berichten',
    description: 'Nieuwe berichten, herinneringen en planning',
    importance: Importance.High,
  });
  await FirebaseMessaging.createChannel({
    id: 'gesprekken',
    name: 'Gesprekken',
    description: 'Persoonlijke berichten van collega’s',
    importance: Importance.High,
  });
  await FirebaseMessaging.createChannel({
    id: 'nood',
    name: 'Nood',
    description: 'Ontruiming en andere noodmeldingen',
    importance: Importance.Max,
  });
}

/**
 * Meldt dit toestel aan bij de server. Bij elke start opnieuw: Firebase kan het token vervangen,
 * en aanmelden is idempotent op het token.
 */
export async function registerNativeDevice(api: ApiClient): Promise<void> {
  await ensureAndroidChannels();
  const { token } = await FirebaseMessaging.getToken();
  const info = await App.getInfo().catch(() => null);
  const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

  await api.POST('/api/v1/push/devices', {
    body: {
      token,
      platform,
      appVersion: info?.version,
      deviceLabel: platform === 'ios' ? 'iPhone (app)' : 'Androidtelefoon (app)',
    },
  });
  storeToken(token);
}

/** Vraagt toestemming en meldt het toestel aan. Geeft de toestemming terug. */
export async function enableNativePush(api: ApiClient): Promise<NativePermission> {
  const { receive } = await FirebaseMessaging.requestPermissions();
  if (receive !== 'granted') return receive === 'denied' ? 'denied' : 'prompt';
  await registerNativeDevice(api);
  return 'granted';
}

/**
 * Meldt dit toestel af: bij uitzetten en bij uitloggen. Faalt nooit hard, want uitloggen moet
 * altijd lukken; de server zet een dood token later zelf uit.
 */
export async function disableNativePush(api: ApiClient): Promise<void> {
  const token = storedNativeToken();
  storeToken(null);
  try {
    if (token) await api.DELETE('/api/v1/push/devices', { body: { token } });
    // Het token zelf ongeldig maken: een volgende gebruiker op dit toestel krijgt een nieuw.
    await FirebaseMessaging.deleteToken();
  } catch {
    // Zie hierboven.
  }
}

/**
 * Een tik op een melding opent het juiste scherm. Geeft een opruimfunctie terug.
 *
 * De gegevens komen uit `data` van het FCM-bericht: `refModule` en `refId`, niets anders.
 */
export function onNotificationTap(open: (path: string) => void): () => void {
  const handle = FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    const data = event.notification.data as { refModule?: string; refId?: string } | undefined;
    open(appPathFor(data?.refModule, data?.refId));
  });
  return () => void handle.then((h) => h.remove());
}
